import { useEffect, useState } from 'react';
import { picturesApi, setPicture, usePictureOf, type PhonePicture } from '../lib/pictures';
import { Face, PhotoEditor } from './PhotoEditor';
import { Button, Dialog } from './ui';

/**
 * A person's photo on their page in People, for the company's admins: shown with Add or Change,
 * which opens the photo editor (choose, crop, save; or remove). Only for people with Stitch access.
 */
export function TheirPhoto({ tenant, userId, name, extension, canChange }: { tenant: string; userId: string; name: string; extension: string | null; canChange: boolean }) {
  const version = usePictureOf(userId);
  const [open, setOpen] = useState(false);
  const [phones, setPhones] = useState<PhonePicture[]>();
  useEffect(() => {
    if (!open) return;
    let alive = true;
    picturesApi.theirs(tenant, userId).then(
      (r) => {
        if (!alive) return;
        setPicture(userId, r.version, { tenant, extension });
        setPhones(r.phones);
      },
      () => undefined
    );
    return () => {
      alive = false;
    };
  }, [open, tenant, userId, extension]);
  return (
    <span className="their-photo">
      <Face userId={userId} name={name} size="small" className="avatar round" />
      {canChange && (
        <Button size="small" variant="ghost" onClick={() => setOpen(true)}>
          {version ? 'Change' : 'Add a photo'}
        </Button>
      )}
      {open && (
        <Dialog
          open
          onClose={() => setOpen(false)}
          title={`${name}’s photo`}
          subtitle="Shows wherever Stitch shows them, and on desk phones when they call coworkers."
          foot={
            <Button variant="primary" onClick={() => setOpen(false)}>
              Done
            </Button>
          }
        >
          <PhotoEditor
            userId={userId}
            name={name}
            whose="their"
            {...(phones ? { phones } : {})}
            save={async (files) => {
              const r = await picturesApi.saveTheirs(tenant, userId, files);
              setPicture(userId, r.version, { tenant, extension });
              setPhones(r.phones);
            }}
            remove={async () => {
              const r = await picturesApi.removeTheirs(tenant, userId);
              setPicture(userId, null, { tenant, extension });
              setPhones(r.phones);
            }}
            onRetry={async () => setPhones((await picturesApi.retryTheirs(tenant, userId)).phones)}
          />
        </Dialog>
      )}
    </span>
  );
}
